import type { DraftState } from '../types';

import { get, set, del } from 'idb-keyval';
import logger from '@/lib/logging';
const STORAGE_PREFIX = 'posture-analysis-draft-';

/**
 * Get the storage key for a client's draft
 */
function getStorageKey(clientId: string | null): string {
  return `${STORAGE_PREFIX}${clientId || 'guest'}`;
}

/**
 * Save draft state to IndexedDB
 */
export async function saveDraft(clientId: string | null, draft: DraftState): Promise<void> {
  try {
    const key = getStorageKey(clientId);
    await set(key, draft);
    logger.debug('Draft saved to IndexedDB', {
      category: 'ui',
      clientId: clientId || 'guest'
    });
  } catch (error) {
    logger.error('Error saving draft to IndexedDB:', error);
    throw error;
  }
}

/**
 * Load draft state from IndexedDB
 */
export async function loadDraft(clientId: string | null): Promise<DraftState | null> {
  try {
    const key = getStorageKey(clientId);
    const draft = await get<DraftState>(key);

    if (!draft) {
      return null;
    }

    // Validate draft structure
    if (!draft.timestamp || !draft.capturedImages || !draft.analysisData || !draft.settings) {
      logger.warn('Invalid draft structure, clearing');
      await clearDraft(clientId);
      return null;
    }

    return draft;
  } catch (error) {
    logger.error('Error loading draft from IndexedDB:', error);
    // Clear corrupted draft
    await clearDraft(clientId);
    return null;
  }
}

/**
 * Clear draft state from IndexedDB
 */
export async function clearDraft(clientId: string | null): Promise<void> {
  try {
    const key = getStorageKey(clientId);
    await del(key);
  } catch (error) {
    logger.error('Error clearing draft from IndexedDB:', error);
  }
}

/**
 * Check if a draft exists for the given client
 */
export async function hasDraft(clientId: string | null): Promise<boolean> {
  try {
    const key = getStorageKey(clientId);
    const draft = await get<DraftState>(key);
    return draft !== undefined;
  } catch (error) {
    logger.error('Error checking draft existence:', error);
    return false;
  }
}

/**
 * Get the timestamp of the draft (if it exists)
 */
export async function getDraftTimestamp(clientId: string | null): Promise<Date | null> {
  try {
    const draft = await loadDraft(clientId);
    if (!draft || !draft.timestamp) {
      return null;
    }
    return new Date(draft.timestamp);
  } catch (error) {
    logger.error('Error getting draft timestamp:', error);
    return null;
  }
}

/**
 * Format timestamp for display (e.g., "5 minutes ago")
 */
export function formatDraftTimestamp(timestamp: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - timestamp.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return timestamp.toLocaleDateString();
  }
}

